/**
 * Minimal fetch wrapper. Throws on non-2xx with the error body parsed.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // Only set Content-Type when we actually have a body — Fastify rejects
  // requests that claim JSON but send nothing (DELETE, POST-without-body).
  const hasBody = init.body !== undefined && init.body !== null;
  const baseHeaders: Record<string, string> = hasBody
    ? { 'Content-Type': 'application/json' }
    : {};
  const res = await fetch(path, {
    credentials: 'include', // send the session cookie
    ...init,
    headers: {
      ...baseHeaders,
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    // 401 → bounce to /login (unless we're already there, to avoid loops).
    // Don't redirect if the request was for /api/auth/me itself; the
    // login screen and "am I logged in?" probe both can 401 normally.
    if (
      res.status === 401 &&
      typeof window !== 'undefined' &&
      window.location.pathname !== '/login' &&
      !path.endsWith('/api/auth/me')
    ) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
    }
    const msg =
      (body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : null) ?? `Request failed: ${res.status}`;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}
